import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";

export default function DesignSettings() {
  const [accentColor, setAccentColor] = useState(() => localStorage.getItem("accent_color") || "#FF10F0");
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("dark_mode") !== "false");
  const [layoutDensity, setLayoutDensity] = useState(() => localStorage.getItem("layout_density") || "comfortable");

  useEffect(() => {
    localStorage.setItem("accent_color", accentColor);
    document.documentElement.style.setProperty("--accent-color", accentColor);
  }, [accentColor]);

  useEffect(() => {
    localStorage.setItem("dark_mode", String(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem("layout_density", layoutDensity);
  }, [layoutDensity]);

  return (
    <AppLayout title="Design Settings" overline="SYSTEM">
      <div className="max-w-4xl">
        <div className="mb-8">
          <p className="text-sm text-zinc-400">Customize the admin interface appearance and behavior</p>
        </div>

        <div className="space-y-6">
          {/* Accent Color Picker */}
          <div className="bg-[#0F1118] border border-white/10 rounded-2xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-white mb-1">Accent Color</h3>
                <p className="text-sm text-zinc-500">Choose your preferred theme color</p>
              </div>
              <div className="w-12 h-12 rounded-xl border-2 border-white/20" style={{ backgroundColor: accentColor }} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { name: "Rose", color: "#E11D48" },
                { name: "Neon Pink", color: "#FF10F0" },
                { name: "Gold", color: "#D4AF37" },
                { name: "Purple", color: "#A855F7" },
                { name: "Blue", color: "#3B82F6" },
                { name: "Emerald", color: "#10B981" },
                { name: "Orange", color: "#F59E0B" },
                { name: "Cyan", color: "#06B6D4" },
              ].map((colorOption) => (
                <button
                  key={colorOption.color}
                  onClick={() => {
                    setAccentColor(colorOption.color);
                    toast.success(`Accent color changed to ${colorOption.name}`);
                  }}
                  data-testid={`accent-color-${colorOption.name.toLowerCase()}`}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 ${
                    accentColor === colorOption.color
                      ? "border-white/40 bg-white/5"
                      : "border-white/10 hover:border-white/20 hover:bg-white/5"
                  }`}
                >
                  <div
                    className="w-10 h-10 rounded-lg"
                    style={{ backgroundColor: colorOption.color }}
                  />
                  <span className="text-xs font-semibold text-zinc-400">{colorOption.name}</span>
                  {accentColor === colorOption.color && (
                    <div className="text-[10px] text-white font-bold">✓ Active</div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Display Mode */}
          <div className="bg-[#0F1118] border border-white/10 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Display Mode</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => {
                  setDarkMode(true);
                  toast.success("Dark mode enabled");
                }}
                data-testid="display-mode-dark"
                className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 ${
                  darkMode
                    ? "border-[#E11D48] bg-[#E11D48]/10"
                    : "border-white/10 hover:border-white/20"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#08090D] to-[#0F1118] border border-white/10 flex items-center justify-center">
                    <div className="w-6 h-6 rounded bg-[#E11D48]" />
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-white">Dark Mode</div>
                    <div className="text-xs text-zinc-500">Perfect for night viewing</div>
                  </div>
                </div>
                {darkMode && <div className="text-[#E11D48] font-bold">✓</div>}
              </button>

              <button
                onClick={() => {
                  setDarkMode(false);
                  toast.info("Light mode coming soon!");
                }}
                data-testid="display-mode-light"
                className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 ${
                  !darkMode
                    ? "border-[#E11D48] bg-[#E11D48]/10"
                    : "border-white/10 hover:border-white/20"
                }`}
                disabled
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-white to-zinc-100 border border-white/20 flex items-center justify-center">
                    <div className="w-6 h-6 rounded bg-[#E11D48]" />
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-white">Light Mode</div>
                    <div className="text-xs text-zinc-500">Coming soon</div>
                  </div>
                </div>
                {!darkMode && <div className="text-[#E11D48] font-bold">✓</div>}
              </button>
            </div>
          </div>

          {/* Layout Density */}
          <div className="bg-[#0F1118] border border-white/10 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Layout Density</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { id: "compact", label: "Compact", desc: "Maximum content, minimal spacing" },
                { id: "comfortable", label: "Comfortable", desc: "Balanced spacing (recommended)" },
                { id: "spacious", label: "Spacious", desc: "Extra breathing room" },
              ].map((option) => (
                <button
                  key={option.id}
                  onClick={() => {
                    setLayoutDensity(option.id);
                    toast.success(`Layout changed to ${option.label}`);
                  }}
                  data-testid={`layout-density-${option.id}`}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                    layoutDensity === option.id
                      ? "border-[#E11D48] bg-[#E11D48]/10"
                      : "border-white/10 hover:border-white/20"
                  }`}
                >
                  <div className="text-left">
                    <div className="font-bold text-white mb-1">{option.label}</div>
                    <div className="text-xs text-zinc-500">{option.desc}</div>
                  </div>
                  {layoutDensity === option.id && (
                    <div className="text-[#E11D48] font-bold text-sm mt-2">✓ Active</div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="text-sm text-blue-300">
              💡 <strong>Tip:</strong> These settings are saved locally in your browser and apply only to your admin interface.
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
